import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {
  NzNotificationModule,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { AuthStateService } from '@hypenation/auth';
import { ApiService } from '../../services';
import {
  Order,
  PaymentMethod,
  Ticket,
  WalletTopUpRequest,
} from '../../models';

/**
 * sessionStorage key used to hand the created order number across the
 * redirect to the external payment gateway and back. The wallet-top-up
 * request body (and therefore its `returnUrl`) is built *before* the order
 * exists, so the order number can't be embedded in the return URL the way
 * the cart/checkout flow does it — the returnUrl here must stay query
 * string free (the backend rewrites any `?`-bearing returnUrl to a bare
 * `/#/?email=` fragment for hash-routed apps), so we hand off the order
 * number locally instead and resolve it on return.
 */
const PENDING_WALLET_TOP_UP_ORDER_KEY = 'hypenationPendingWalletTopUpOrder';

interface PendingWalletTopUpOrder {
  orderNumber: string;
  ticketUid: string;
}

@Component({
  selector: 'hypenation-tickets-wallet-top-up',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    NzNotificationModule,
  ],
  templateUrl: './wallet-top-up.component.html',
  styleUrl: './wallet-top-up.component.css',
})
export class WalletTopUpComponent implements OnInit {
  form: FormGroup;
  submitting = false;

  // True when this instance is rendered at the post-payment return route
  // rather than the top-up form itself.
  isConfirmationMode = false;
  resolvedOrderNumber: string | null = null;

  /**
   * The ticket being topped up. Required — My Tickets always links here with it, and it is the only
   * reference that identifies a ticket unambiguously. A ticket NUMBER is not a key: numbers repeat
   * across events, and on events predating per-event uniqueness they repeat across ticket types
   * within one event too, so a number cannot be resolved safely even with an event to scope it by.
   * The backend now rejects a number without an eventUid outright.
   */
  ticketUid: string | null = null;

  /** Display only — never sent. Shows the customer which ticket they are topping up. */
  ticketNumberLabel: string | null = null;

  /**
   * Resolved from the ticket's ticket type, never chosen. A customer picking the currency could
   * fund a wallet in one the ticket does not trade in, leaving a balance the booths cannot spend
   * against. Null until resolved, which is what {@link resolving} gates the form on.
   */
  currencyCode: string | null = null;

  /** True while the wallet and ticket type are being read; the form is not submittable yet. */
  resolving = true;

  private readonly _fb = inject(FormBuilder);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _apiService = inject(ApiService);
  private readonly _authStateService = inject(AuthStateService);
  private readonly _notification = inject(NzNotificationService);

  constructor() {
    // Amount, email and contact number are the only things the customer supplies. The ticket comes
    // from the uid this page was opened with, and the currency from that ticket's ticket type.
    this.form = this._fb.group({
      amount: [null, [Validators.required, Validators.min(0.01)]],
      email: ['', [Validators.required, Validators.email]],
      contactNumber: [''],
    });
  }

  ngOnInit(): void {
    this.isConfirmationMode =
      this._route.snapshot.data['mode'] === 'confirmation';

    if (this.isConfirmationMode) {
      this.resolvePendingOrder();
      return;
    }

    this.ticketUid = this._route.snapshot.queryParamMap.get('ticketUid');
    this.ticketNumberLabel =
      this._route.snapshot.queryParamMap.get('ticketNumber');

    if (!this.ticketUid) {
      // Without a uid there is nothing safe to top up. A ticketNumber-only link cannot be served:
      // the backend requires an eventUid alongside a number, and even with one, a legacy event can
      // match the same number on several ticket types. Send the customer to pick the ticket instead
      // of letting them fill in a form whose submit can only fail.
      this._notification.info(
        'Choose a ticket',
        'Open the ticket you want to top up from My Tickets.'
      );
      this._router.navigate(['/my-tickets'], { replaceUrl: true });
      return;
    }

    const email = this._authStateService.getUserEmail();
    if (email) {
      this.form.patchValue({ email });
    }

    this.resolveCurrency(this.ticketUid);

    this._apiService.scrollToTop();
  }

  /**
   * Confirms the ticket has a wallet, then reads the ticket's own currency for display.
   *
   * <p>The wallet read fails here, before the customer fills anything in, when no wallet exists —
   * previously that only surfaced as a server error after they pressed Top Up.
   *
   * <p>Currency comes from the TICKET, not its ticket type. A free ticket type carries
   * {@code currencyCode: null} while the ticket issued from it still records one, so the type
   * cannot be relied on — a free ticket whose wallet had never been funded had no resolvable
   * currency at all and the form dead-ended. This is also the exact value ecommerce-service derives
   * server-side, so client and server cannot disagree.
   *
   * <p>Display only: the server decides the currency regardless, so a failure here never blocks the
   * top-up. When it cannot be read we simply omit it and let the server supply it.
   */
  private resolveCurrency(ticketUid: string): void {
    this._apiService.getMyTicketWalletBalance(ticketUid).subscribe({
      next: () => {
        this._apiService.getTicketByUid(ticketUid).subscribe({
          next: (ticketRes: HttpResponse<Ticket>) => {
            this.currencyCode = ticketRes.body?.currencyCode ?? null;
            this.resolving = false;
          },
          error: () => {
            // Cosmetic only — the server denominates the top-up from the ticket either way.
            this.currencyCode = null;
            this.resolving = false;
          },
        });
      },
      error: (error: HttpErrorResponse) => {
        this.resolving = false;
        this._notification.error(
          'Top-up unavailable',
          error.status === 404
            ? 'This ticket does not have a wallet yet, so it cannot be topped up.'
            : this.describeFailure(error)
        );
        this._router.navigate(['/my-tickets'], { replaceUrl: true });
      },
    });
  }

  /**
   * Resolves the order created by the top-up request that was in-flight
   * before the customer was redirected to the gateway. When found, hands
   * off to the existing order-confirmation page (which already knows how
   * to poll for payment completion). Falls back to a generic message when
   * there's no local pointer (e.g. return on a different device/browser).
   */
  private resolvePendingOrder(): void {
    const raw = sessionStorage.getItem(PENDING_WALLET_TOP_UP_ORDER_KEY);
    sessionStorage.removeItem(PENDING_WALLET_TOP_UP_ORDER_KEY);

    if (raw) {
      try {
        const pending = JSON.parse(raw) as PendingWalletTopUpOrder;
        if (pending?.orderNumber) {
          this._router.navigate(['/order-confirmation', pending.orderNumber], {
            replaceUrl: true,
          });
          return;
        }
      } catch {
        // Ignore malformed pointer, fall through to the generic message.
      }
    }

    this.resolvedOrderNumber = null;
  }

  submit(): void {
    // Wait for the wallet check, but do NOT require a currency: the server derives it from the
    // ticket, so an unresolved one is a display gap rather than a blocker.
    if (this.resolving) {
      return;
    }
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      });
      return;
    }

    const value = this.form.value;

    // Deliberately no query string — see PENDING_WALLET_TOP_UP_ORDER_KEY note above.
    const returnUrl = `${window.location.origin}/wallet-top-up/confirmation`;

    const payload: WalletTopUpRequest = {
      // Always the uid. ngOnInit has already redirected away when there isn't one, so there is no
      // ticketNumber branch left to fall back to.
      ticketUid: this.ticketUid as string,
      amount: value.amount,
      // The ticket's own currency, read in ngOnInit — not a form value. Omitted when it could not
      // be read: the server derives the same value from the ticket, and sending a guess would be
      // refused as a mismatch.
      ...(this.currencyCode ? { currencyCode: this.currencyCode } : {}),
      email: value.email,
      contactNumber: value.contactNumber || undefined,
      paymentMethod: PaymentMethod.ONLINE,
      returnUrl,
    };

    this.submitting = true;
    this._apiService.initiateWalletTopUp(payload).subscribe({
      next: (res: HttpResponse<Order>) => {
        const order = res.body;
        if (!order) {
          this.submitting = false;
          this._notification.error(
            'Top-up failed',
            'The top-up could not be started. Please try again.'
          );
          return;
        }

        const pending: PendingWalletTopUpOrder = {
          orderNumber: order.orderNumber,
          ticketUid: this.ticketUid as string,
        };
        sessionStorage.setItem(
          PENDING_WALLET_TOP_UP_ORDER_KEY,
          JSON.stringify(pending)
        );

        // The create response can never carry the gateway URL. `Order.paymentTransaction` is a
        // read-only association (`insertable = false, updatable = false`) on the very column that
        // payment initiation writes as the scalar `paymentTransactionId`, and the entity was loaded
        // before that id existed — so the association is null on the way out, every time. Re-read
        // the order and take the redirect off the fresh load, which is what the checkout flow has
        // always done. Reading it off the create response is why self-funding dead-ended on an
        // unpayable order (QA finding NEW-7).
        this._apiService.getOrdersByOrderNumber(order.orderNumber).subscribe({
          next: (fresh: HttpResponse<Order>) => {
            const redirectUrl = fresh.body?.paymentTransaction?.redirectUrl;
            if (redirectUrl) {
              window.location.href = redirectUrl;
              return;
            }
            // Genuinely no redirect (e.g. the gateway already settled it) — fall back to the
            // standard order-confirmation polling page.
            this.submitting = false;
            this._router.navigate(['/order-confirmation', order.orderNumber]);
          },
          error: () => {
            this.submitting = false;
            this._router.navigate(['/order-confirmation', order.orderNumber]);
          },
        });
      },
      // Previously `error: () => { this.submitting = false; }` — the spinner simply stopped and the
      // customer was told nothing at all (QA finding BUG-12). The server's own message is the useful
      // one here: it distinguishes "wallets are not enabled for this event" from "no wallet exists
      // yet" from an ambiguous ticket number, and a customer who sees that can act on it.
      error: (error: HttpErrorResponse) => {
        this.submitting = false;
        this._notification.error('Top-up failed', this.describeFailure(error));
      },
    });
  }

  /**
   * Prefer the API's message for a 4xx — those are written for the customer and say which of the
   * several wallet preconditions failed ("wallets are not enabled for this event", "no wallet exists
   * yet", an ambiguous ticket number), and a customer who sees that can act on it.
   *
   * Never echo the body of a 5xx. A server fault carries an internal failure chain, not customer
   * copy: the wallet-top-up 500 renders as
   * `[500] during [GET] to [http://merchants-service/pvt/internal/v1/ticket-wallets/ticket/{uid}]
   * [MerchantsModuleClient#getTicketWalletByTicketUid(String)]`, which tells the customer nothing
   * and tells everyone else the internal hostnames, private endpoint paths and client method names
   * (QA finding NEW-9).
   */
  private describeFailure(error: HttpErrorResponse): string {
    const status = error?.status ?? 0;

    if (status === 0) {
      return 'We could not reach the server. Check your connection and try again.';
    }
    if (status >= 500) {
      return 'Something went wrong on our side and the top-up was not started. Nothing has been charged — please try again shortly.';
    }
    if (status === 404) {
      return 'That ticket could not be found. Start the top-up from the ticket in My Tickets.';
    }

    const message = error?.error?.message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
    return 'The top-up could not be started. Please try again.';
  }
}
